// ============================================================
// AI Search — Minimax with Alpha-Beta Pruning + Time Control
// ============================================================

import { type BoardState, type Move } from '@core/types';
import { generateLegalMoves, applyMove } from '@core/moves';
import { evaluate } from './evaluator';

export interface SearchResult {
  move: Move | null;
  score: number;
  nodesSearched: number;
  depth: number;
  timeMs: number;
}

const MAX_TIME_MS = 3000; // 3 second hard limit

/** Iterative deepening search with time control */
export function search(state: BoardState, maxDepth: number): SearchResult {
  const startTime = performance.now();
  let bestResult: SearchResult = { move: null, score: 0, nodesSearched: 0, depth: 0, timeMs: 0 };
  let aborted = false;

  // Generate and order moves once at root
  const rootMoves = generateLegalMoves(state);
  if (rootMoves.length === 0) return bestResult;
  if (rootMoves.length === 1) {
    // Only one legal move — play it instantly
    return { move: rootMoves[0], score: 0, nodesSearched: 1, depth: 1, timeMs: 0 };
  }

  // Iterative deepening: depth 1 → maxDepth
  for (let depth = 1; depth <= maxDepth; depth++) {
    if (aborted) break;

    let nodesSearched = 0;
    const maximizing = state.activePlayer === 'white';
    let bestMove = rootMoves[0];
    let bestScore = maximizing ? -Infinity : Infinity;

    for (const move of rootMoves) {
      if (performance.now() - startTime > MAX_TIME_MS) {
        aborted = true;
        break;
      }

      const next = applyMove(state, move);
      nodesSearched++;
      const score = alphaBeta(next, depth - 1, -Infinity, Infinity, !maximizing, startTime, () => { nodesSearched++; });

      if (score === null) {
        aborted = true;
        break;
      }

      if (maximizing ? score > bestScore : score < bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }

    if (!aborted) {
      bestResult = {
        move: bestMove,
        score: bestScore,
        nodesSearched,
        depth,
        timeMs: performance.now() - startTime,
      };
    }
  }

  bestResult.timeMs = performance.now() - startTime;
  return bestResult;
}

function alphaBeta(
  state: BoardState,
  depth: number,
  alpha: number,
  beta: number,
  maximizing: boolean,
  startTime: number,
  countNode: () => void,
): number | null {
  // Time check every node
  if (performance.now() - startTime > MAX_TIME_MS) return null;

  countNode();

  if (depth === 0) return evaluate(state);

  const moves = generateLegalMoves(state);
  if (moves.length === 0) {
    // Checkmate or stalemate
    return maximizing ? -99999 + depth : 99999 - depth;
  }

  // Move ordering: captures first, checks second, cross-dim last
  // Limit branching: at depth > 1, cap move count to reduce explosion
  orderMoves(moves);
  const maxMoves = depth >= 3 ? 30 : depth >= 2 ? 40 : moves.length;
  const limited = moves.length > maxMoves ? moves.slice(0, maxMoves) : moves;

  if (maximizing) {
    let maxEval = -Infinity;
    for (const move of limited) {
      const next = applyMove(state, move);
      const val = alphaBeta(next, depth - 1, alpha, beta, false, startTime, countNode);
      if (val === null) return null; // timeout
      maxEval = Math.max(maxEval, val);
      alpha = Math.max(alpha, val);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of limited) {
      const next = applyMove(state, move);
      const val = alphaBeta(next, depth - 1, alpha, beta, true, startTime, countNode);
      if (val === null) return null; // timeout
      minEval = Math.min(minEval, val);
      beta = Math.min(beta, val);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}

function orderMoves(moves: Move[]): void {
  moves.sort((a, b) => {
    const aScore = (a.capture ? 10000 + (a.capture.kind === 'queen' ? 900 : a.capture.kind === 'rook' ? 500 : 300) : 0)
                 + (a.isCheck ? 5000 : 0)
                 + (a.promotion ? 8000 : 0)
                 + (a.isCrossDimensional ? -50 : 0); // deprioritize cross-dim (expensive branches)
    const bScore = (b.capture ? 10000 + (b.capture.kind === 'queen' ? 900 : b.capture.kind === 'rook' ? 500 : 300) : 0)
                 + (b.isCheck ? 5000 : 0)
                 + (b.promotion ? 8000 : 0)
                 + (b.isCrossDimensional ? -50 : 0);
    return bScore - aScore;
  });
}
